import { useCallback, useEffect, useRef } from "react";
import SignaturePad from "signature_pad";

/**
 * Export/backing-store scale bounds: floored at 1 (a zoomed-out sub-1 DPR
 * must never shrink the export) and CLAMPED at 2 — ultra-high-DPR devices
 * would otherwise export multi-megabyte data-URI metas into the data tree.
 */
const MIN_EXPORT_SCALE = 1;
const MAX_EXPORT_SCALE = 2;

/** signature_pad's own transparent default — kept byte-identical so the
 * mount-time background effect is a no-op when no background is given. */
const TRANSPARENT_BACKGROUND = "rgba(0,0,0,0)";

const isNonBlank = (value: string | undefined): value is string =>
  typeof value === "string" && value.trim() !== "";

/** Unset penColor resolves to the canvas's computed text color (the
 * foreground token), so signatures stay legible in both color modes. */
const resolvePenColor = (
  canvas: HTMLCanvasElement,
  penColor: string | undefined,
): string => {
  if (isNonBlank(penColor)) return penColor;

  const computed = window.getComputedStyle(canvas).color;

  return isNonBlank(computed) ? computed : "#000000";
};

interface UseSignaturePadArgs {
  backgroundColor?: string;
  /** false when disabled OR loading — unbinds the library's input handlers */
  isInteractive: boolean;
  onClear?: () => void;
  onStrokeEnd?: (dataURL: string) => void;
  penColor?: string;
  value?: string;
}

/**
 * Owns the `signature_pad` instance lifecycle behind refs. ALL drawing state
 * lives in the library and these refs — nothing per-pointermove (or per any
 * stroke event) touches React state; the only exits are the onStrokeEnd /
 * onClear callbacks, which fire once per completed gesture.
 */
export function useSignaturePad(args: UseSignaturePadArgs) {
  const { backgroundColor, isInteractive, onClear, onStrokeEnd, penColor } =
    args;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const padRef = useRef<SignaturePad | null>(null);
  const onStrokeEndRef = useRef(onStrokeEnd);
  const onClearRef = useRef(onClear);

  useEffect(function trackLatestCallbacks() {
    onStrokeEndRef.current = onStrokeEnd;
    onClearRef.current = onClear;
  });

  /**
   * (Re)size the canvas backing store to layout size × clamped DPR. The 2D
   * context resets on resize, so existing strokes are preserved by a VECTOR
   * round-trip (toData → fromData — point groups, never an image sink).
   */
  const rescale = useCallback(() => {
    const canvas = canvasRef.current;
    const pad = padRef.current;

    if (canvas === null || pad === null) return;

    const scale = Math.min(
      Math.max(window.devicePixelRatio || 1, MIN_EXPORT_SCALE),
      MAX_EXPORT_SCALE,
    );
    const strokes = pad.toData();

    canvas.width = canvas.offsetWidth * scale;
    canvas.height = canvas.offsetHeight * scale;
    canvas.getContext("2d")?.scale(scale, scale);

    // clear() repaints the background into the resized backing store.
    pad.clear();

    if (strokes.length > 0) {
      pad.fromData(strokes);
    }
  }, []);

  // Instance lifecycle. Declared FIRST so the option effects below always
  // see padRef populated on mount.
  useEffect(
    function initializeSignaturePad() {
      const canvas = canvasRef.current;

      if (canvas === null) return;

      const pad = new SignaturePad(canvas);

      padRef.current = pad;

      const handleEndStroke = () => {
        onStrokeEndRef.current?.(pad.toDataURL("image/png"));
      };

      pad.addEventListener("endStroke", handleEndStroke);
      rescale();

      return () => {
        pad.removeEventListener("endStroke", handleEndStroke);
        pad.off();
        padRef.current = null;
      };
    },
    [rescale],
  );

  useEffect(
    function applyPenColor() {
      const canvas = canvasRef.current;
      const pad = padRef.current;

      if (canvas === null || pad === null) return;

      pad.penColor = resolvePenColor(canvas, penColor);
    },
    [penColor],
  );

  useEffect(
    function applyBackgroundColor() {
      const pad = padRef.current;

      if (pad === null) return;

      const next = isNonBlank(backgroundColor)
        ? backgroundColor
        : TRANSPARENT_BACKGROUND;

      if (pad.backgroundColor === next) return;

      pad.backgroundColor = next;

      // Repaint under the existing strokes (vector round-trip, no image sink).
      const strokes = pad.toData();

      pad.clear();

      if (strokes.length > 0) {
        pad.fromData(strokes);
      }
    },
    [backgroundColor],
  );

  useEffect(
    function toggleInteractivity() {
      const pad = padRef.current;

      if (pad === null) return;

      if (isInteractive) {
        pad.on();
      } else {
        pad.off();
      }
    },
    [isInteractive],
  );

  useEffect(
    function observeContainerResize() {
      const canvas = canvasRef.current;

      // Guard: environments without ResizeObserver simply keep the
      // mount-time scale.
      if (canvas === null || typeof ResizeObserver === "undefined") return;

      const observer = new ResizeObserver(() => rescale());

      observer.observe(canvas);

      return () => observer.disconnect();
    },
    [rescale],
  );

  /** Clear button handler: clearing an EMPTY pad is a no-op — it must not
   * fire onClear and phantom-dirty the enclosing form. */
  const clear = useCallback(() => {
    const pad = padRef.current;

    if (pad === null || pad.isEmpty()) return;

    pad.clear();
    onClearRef.current?.();
  }, []);

  /** External clear signal (value transitioned to unset). Never draws. */
  const clearIfUnset = useCallback((value: string | undefined) => {
    const pad = padRef.current;

    if (pad === null) return;

    if (!isNonBlank(value) && !pad.isEmpty()) {
      pad.clear();
    }
  }, []);

  return { canvasRef, clear, clearIfUnset };
}
